// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Customer } from '@/customer/api/types';
import { Typography } from '@mui/material';

type ProjectCustomer = Pick<Customer, 'name'>;

/**
 * Returns the label color for the customer.
 *
 * @param customer - A nullable project customer.
 * @param isArchived - If true, the project is archived.
 */
function getColor(
  customer: ProjectCustomer | null,
  isArchived: boolean | undefined
): string {
  if (isArchived) {
    return 'text.disabled';
  }

  return customer === null ? 'text.secondary' : 'text.primary';
}

interface Props {
  /**
   * The customer to display.
   */
  customer: ProjectCustomer | null;

  /**
   * If true, the project is archived and the label will use the disabled color.
   */
  isArchived?: boolean;
}

/**
 * A label component that renders the name of a project's customer or an empty
 * state if the customer is null.
 */
export function ProjectCustomerLabel(props: Props) {
  const label = props.customer === null ? 'Not set' : props.customer.name;
  const color = getColor(props.customer, props.isArchived);

  return (
    <Typography variant="body2" color={color}>
      {label}
    </Typography>
  );
}
